import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { articlesAPI, uploadsAPI } from '../services/api';
import { usePageMeta } from '../services/seo';
import { useT } from '../services/i18n';
import { localizeArticle } from '../services/catalogI18n';
import ShareButtons from '../components/ShareButtons';

const ArticlePage = () => {
  const { id } = useParams();
  const { t, lang } = useT();
  const [raw, setRaw] = useState(null);
  const [all, setAll] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setRaw(null); setNotFound(false); setLoadError(false);
    articlesAPI.getById(id).then(setRaw).catch((e) => { if (e.response?.status === 404) setNotFound(true); else setLoadError(true); });
    articlesAPI.getAll().then(setAll).catch(() => {});
    window.scrollTo(0, 0);
  }, [id]);

  // Real reading time (only while the tab is visible) + max scroll depth, sent once when leaving the guide
  useEffect(() => {
    let visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
    let seconds = 0;
    let depth = 0;
    let sent = false;
    const measure = () => {
      const doc = document.documentElement;
      const max = Math.max(1, doc.scrollHeight - window.innerHeight);
      depth = Math.max(depth, Math.min(1, (window.scrollY + window.innerHeight * 0.1) / max));
    };
    const flush = () => {
      if (visibleSince) { seconds += (Date.now() - visibleSince) / 1000; visibleSince = null; }
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') { flush(); send(); } else visibleSince = Date.now(); };
    const send = () => { if (sent) return; flush(); if (seconds >= 3) { sent = true; articlesAPI.engagement(id, Math.round(seconds), Number(depth.toFixed(2))); } };
    window.addEventListener('scroll', measure, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', send);
    measure();
    return () => { window.removeEventListener('scroll', measure); document.removeEventListener('visibilitychange', onVisibility); window.removeEventListener('pagehide', send); send(); };
  }, [id]);

  const article = raw ? localizeArticle(raw, lang) : null;
  // noindex only on a confirmed 404 — never while loading or on a transient error (Googlebot would otherwise drop the page)
  usePageMeta(article ? { title: article.title, description: article.description, image: article.image, type: 'article', path: `/article/${id}` } : notFound ? { title: t('blog.notFound'), noindex: true } : { path: `/article/${id}` });
  const related = all.filter(a => a.id !== id).slice(0, 2).map(a => localizeArticle(a, lang));

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50"><Header /><main className="max-w-3xl mx-auto px-4 py-16 text-center text-gray-600" data-testid="article-load-error"><p>{t('common.loadError')}</p><button type="button" onClick={() => window.location.reload()} className="mt-4 text-orange-600 underline" data-testid="article-retry">{t('common.retry')}</button></main><Footer /></div>
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-testid="article-not-found">
          <h1 className="text-2xl font-bold text-gray-900">{t('blog.notFound')}</h1>
          <Link to="/" className="text-blue-600 hover:underline mt-4 inline-block">{t('content.backHome')}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-8" data-testid="article-page">
        <div className="mb-6">
          <Link to="/articles" className="flex items-center gap-2 text-blue-600 hover:underline" data-testid="article-back-link"><ArrowLeft size={18} /> {t('blog.backToList')}</Link>
        </div>
        {!article ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : (
          <article className="bg-white rounded-lg shadow-md overflow-hidden">
            {article.image && <div className="aspect-video"><img src={uploadsAPI.resolveUrl(article.image)} alt={article.title} className="w-full h-full object-cover" /></div>}
            <div className="p-8">
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                <span className="flex items-center gap-1"><Calendar size={16} /> {article.date}</span>
                <span className="flex items-center gap-1"><User size={16} /> {article.author || 'OTOPIECES.COM'}</span>
                <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">{t('blog.readTime', { time: article.read_time || '5 min' })}</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4" data-testid="article-title">{article.title}</h1>
              <div className="mb-8 pb-8 border-b border-gray-200">
                <ShareButtons kind="article" itemId={id} payload={{ article_id: id }} gaParams={{ item_name: article.title }} networks={['twitter', 'linkedin']} testId="article-share" title={article.title} url={`${window.location.origin}/article/${id}${lang !== 'fr' ? `?lang=${lang}` : ''}`} />
              </div>
              <div className="prose prose-lg max-w-none" data-testid="article-content" dangerouslySetInnerHTML={{ __html: article.content || `<p>${article.description}</p>` }} />
            </div>
          </article>
        )}
        {related.length > 0 && (
          <section className="mt-12" data-testid="article-related">
            <h2 className="text-xl font-bold text-gray-900 mb-6">{t('blog.related')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {related.map(r => (
                <Link key={r.id} to={`/article/${r.id}`} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow group" data-testid={`article-related-${r.id}`}>
                  <div className="aspect-video overflow-hidden"><img src={uploadsAPI.resolveUrl(r.image)} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /></div>
                  <div className="p-4">
                    <p className="text-sm text-gray-500 mb-2">{r.date}</p>
                    <h3 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">{r.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ArticlePage;
